import * as Sentry from '@sentry/nextjs';
import { CONTACT_DETAILS_PHRASES } from '@/consts/phrases/registrationPhrases';
import { registerService } from '@/services/api/registerService/registerService';
import {
    getFetchErrors,
    getGeneralFetchErrorPhraseId,
    getCooldownFromError,
} from '@/utils/getFetchErrorMessage';
import { ContactDetailsFormValues } from './ContactDetailsForm';
import { usePhrase } from '@/hooks/general';

export const sendMobileCode = async (
    registrationId: string | null,
    mobilePhoneFormVal: string | undefined,
    recaptchaResponse: string | null | undefined,
    setPhoneVerificationErrorMessage: (errMessage: string | undefined) => void,
    countryCode: string | undefined,
    handleServerErrors: (err: unknown) => void,
    clearAllErrors: () => void,
    t: ReturnType<typeof usePhrase>['t'],
): Promise<boolean | { secondsLeft: number }> => {
    try {
        if (!registrationId) {
            Sentry.captureException(
                new Error('Attempted to submit form but no registration id existed'),
            );
            return false;
        }
        if (!mobilePhoneFormVal || !countryCode) {
            setPhoneVerificationErrorMessage(
                t(CONTACT_DETAILS_PHRASES.INVALID_MOBILE_PHONE_NUMBER) as string,
            );
            return false;
        }
        if (!recaptchaResponse) {
            setPhoneVerificationErrorMessage(
                t(CONTACT_DETAILS_PHRASES.INVALID_CAPTCHA_RESPONSE) as string,
            );
            return false;
        }
        await registerService.verifyPhone({
            registrationId,
            mobilePhone: mobilePhoneFormVal,
            captchaResponse: recaptchaResponse,
        });
        clearAllErrors();
        setPhoneVerificationErrorMessage(undefined);
        return true;
    } catch (err) {
        if (getGeneralFetchErrorPhraseId(err)) {
            setPhoneVerificationErrorMessage(
                t(getGeneralFetchErrorPhraseId(err), {
                    replaceTextVars: getFetchErrors(err)?.generalErrors[0].phrasePlaceholders,
                }) as string,
            );
        } else {
            handleServerErrors(err);
        }
        const cooldown = getCooldownFromError(err);
        if (cooldown) return cooldown;

        return false;
    }
};

export const submitContactDetailsData = async (
    registrationId: string | null,
    recaptchaResponse: string | undefined | null,
    setRecaptchaError: (errMessage: number | undefined) => void,
    values: Partial<ContactDetailsFormValues>,
    goToNextStep: () => void,
    reset: () => void,
    handleError: (err: unknown) => void,
    revalidateRegistrationData: () => Promise<void>,
) => {
    try {
        if (!registrationId) {
            Sentry.captureException(
                new Error('Attempted to submit form but no registration id existed'),
            );
            reset();
            return;
        }
        if (!recaptchaResponse) {
            setRecaptchaError(CONTACT_DETAILS_PHRASES.CAPTCHA_RESPONSE_REQUIRED_ERROR);
            return;
        }
        setRecaptchaError(undefined);
        await registerService.putContactDetails(registrationId, {
            ...values,
            captchaResponse: recaptchaResponse,
        });
        goToNextStep();
        await revalidateRegistrationData();
    } catch (err) {
        handleError(err);
    }
};
